import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';

/** Example prompts, grouped by the kind of question. These are the read-only
 *  things the assistant answers by reading your live, tenant-scoped data or the
 *  product knowledge base — nothing here changes anything. */
const ASK: { topic: string; example: string }[] = [
  ['Clients', 'Find the client called Acme. / Which clients are still on a trial?'],
  ['Subscriptions', 'Show all active subscriptions for Acme. / What is renewing next month?'],
  ['Invoices', "List Acme's overdue invoices. / How many invoices are still pending?"],
  [
    'Revenue analytics',
    'What is our current MRR and how has it moved over 6 months? / What is our ARR by plan? / What is our monthly churn?',
  ],
  [
    'Catalog',
    'List our bundles and which are published. / What metrics do we meter? / Which regions and currencies do we sell in?',
  ],
  [
    'Metered usage',
    'Which usage quantities are missing in arrears? / How many seats is Acme using this period?',
  ],
  [
    'How Revvly works',
    'How is MRR calculated? / What is the difference between volume-tiered and graduated pricing?',
  ],
  [
    'Pricing guidance',
    'Which pricing model should I use for seat-based billing with an included allowance?',
  ],
  [
    'OKR hypotheses',
    'Which Key Results are falling behind this cycle? / What have we already tried on New deals?',
  ],
].map(([topic, example]) => ({ topic, example }));

/** High-impact writes the assistant proposes. These wait for a confirmation
 *  card. Reversible WRITE-LOW actions (notes, tasks, logged emails) run inline
 *  and are not listed here. */
const ACTIONS: { name: string; detail: string }[] = [
  [
    'Create a client',
    'Add a customer organization — with billing details, a trial window, and contacts.',
  ],
  [
    'Create a subscription',
    'Subscribe a client to a bundle, modules, or variable components, priced from your catalog.',
  ],
  [
    'Create a bundle',
    'Draft a new plan customers can buy, with its modules and per-region base prices.',
  ],
  ['Create a module', 'Draft a capability group, optionally with priced tiers.'],
  ['Create a metric', 'Define a metered unit — seats, GB, transactions — to bill usage against.'],
  [
    'Create a variable component',
    'Wrap a metric in a usage-based pricing model (flat, tiered, overage, commission, …).',
  ],
  [
    'Publish a variable component',
    'Freeze a draft component so it can be attached to a module or bundle.',
  ],
  ['Publish a module', 'Freeze a draft module (once fully priced) so it can be added to a bundle.'],
  [
    'Publish a bundle',
    'Freeze a draft bundle (once fully priced) so it can be sold on a subscription.',
  ],
  [
    'Attach a variable component',
    'Add a published component to a draft module or bundle, with its inclusions and price.',
  ],
  [
    'Create a new draft version',
    'Branch an editable draft from a published bundle, module, or component — the way to change a bundle or module, since published catalog entities are immutable. A branched component still needs its structure edited in the dashboard before the draft is published.',
  ],
  ['Enter a usage quantity', 'Seal a missing in-arrears quantity on a closed billing period.'],
  [
    'Waive a usage period',
    'Bill nothing for a closed period that has no usage, with a recorded reason.',
  ],
  [
    'Propose hypotheses for a Key Result',
    'For a Key Result falling behind, up to three testable bets grounded in its numbers and past learnings, added to Backlog. It never claims a bet will hit the target, and avoids bets already tried.',
  ],
].map(([name, detail]) => ({ name, detail }));

export function RevvlyAgentGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'The Revvly Assistant — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs">Docs</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Assistant</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Assistant</span>
        <h1>
          The Revvly <em>Assistant</em>.
        </h1>
        <p className="dp-oneliner">
          An AI assistant built into your dashboard. Ask it about your subscription business in
          plain language, and have it set up clients, catalog, and subscriptions for you — you
          review and confirm irreversible or high-impact changes before they happen.
        </p>
      </header>

      <div className="dp-section">
        <h2>
          <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>01</span>
          What it is
        </h2>
        <p className="dp-plain">
          The Assistant lives in the dashboard as a chat panel. It reads the same data you see —
          scoped entirely to your workspace, never another tenant&rsquo;s — and it knows the
          product: pricing models, catalog structure, billing timing, renewals, and how metrics like
          MRR and churn are defined. Ask it a question and it fetches the live numbers; ask it to
          build something and it drafts the change for your approval. It answers with the takeaway
          plus cards — metric tiles, charts, and tables — beside the reply.
        </p>
      </div>

      <div className="dp-section">
        <h2>
          <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>02</span>
          What you can ask it
        </h2>
        <p className="dp-section-sub">
          Read-only questions — answered from your live data or the product knowledge base. Nothing
          here changes anything.
        </p>
        <div className="dp-fields-table">
          {ASK.map((row, i) => (
            <div className="dp-field-row" key={i}>
              <div className="dp-field-k">{row.topic}</div>
              <div className="dp-field-v">{row.example}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="dp-section">
        <h2>
          <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>03</span>
          What it can do
        </h2>
        <p className="dp-section-sub">
          High-impact actions wait for a confirmation card you approve or decline. Reversible writes
          — notes, logged emails, creating a prospect or task — run immediately.
        </p>
        <div className="dp-fields-table">
          {ACTIONS.map((row, i) => (
            <div className="dp-field-row" key={i}>
              <div className="dp-field-k">{row.name}</div>
              <div className="dp-field-v">{row.detail}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="dp-section">
        <h2>
          <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>04</span>
          How a change happens
        </h2>
        <p className="dp-plain">
          Reversible writes run as soon as the Assistant does them — no card. For a high-impact
          change, it looks up what it needs (say, your regions), then shows a{' '}
          <strong>confirmation card</strong> summarizing exactly what it will create — with names,
          not ids. Nothing on that card is written until you confirm. On confirm, the change runs
          through the same validation as the manual screens (name uniqueness, region and currency
          checks, catalog pricing), and the Assistant reports back with a link to the new record.
          Decline it and nothing happens. A proposal is valid for 15 minutes, after which it cannot
          be approved. Each mutation is its own proposal: approving one does not approve the next,
          and a stale proposal whose underlying record has moved since it was staged is refused
          rather than writing over someone else&rsquo;s edit.
        </p>
        <Figure
          title="A high-impact change"
          caption="Nothing is written until you confirm, and a late or outdated confirmation is refused."
        >
          <Flow
            steps={[
              { title: 'You ask', text: 'For a high-impact change.' },
              {
                title: 'Confirmation card',
                text: 'Exactly what it will create, with names, not ids.',
              },
              {
                fork: [
                  {
                    title: 'You confirm',
                    text: 'Runs through the same validation as the manual screens.',
                    tone: 'accent',
                  },
                  { title: 'You decline', text: 'Nothing happens.', tone: 'muted' },
                  {
                    title: 'After 15 minutes',
                    text: 'The proposal cannot be approved.',
                    tone: 'warn',
                  },
                  {
                    title: 'The record has moved',
                    text: 'Refused, rather than overwriting someone else’s edit.',
                    tone: 'warn',
                  },
                ],
              },
              {
                title: 'Assistant reports back',
                text: 'With a link to the new record.',
                tone: 'accent',
              },
            ]}
          />
        </Figure>
      </div>

      <div className="dp-section">
        <h2>
          <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>05</span>
          Building catalog in the right order
        </h2>
        <p className="dp-plain">
          Usage-based pricing is built in steps, and the Assistant walks them in order across a few
          confirmations: create a metric, wrap it in a variable component, publish the component,
          then attach it to a module or bundle with its price. A component must be published before
          it can be attached, a module must be published before it can join a bundle, and a bundle
          must be published before it can be sold on a subscription — published catalog items are
          immutable, so you attach into drafts. If you ask to attach, sell, or subscribe to
          something still in draft, the Assistant proposes publishing it first.
        </p>
        <p className="dp-plain" style={{ marginTop: 14 }}>
          Asking to change a published bundle or module — attach a component to a live bundle,
          adjust a module&rsquo;s billing periods — is not a dead end. The Assistant branches a new
          draft version from it first, carrying over its modules, tiers, attached components and
          prices, applies your change to that draft, and proposes publishing it once you approve.
          The version you had stays exactly as it was until the new one ships. A published variable
          component can be branched the same way, but chat has no tool to edit its structure — the
          Assistant will point you to the component&rsquo;s page in the dashboard to finish that
          edit before the draft is published.
        </p>
        <ul className="dp-connects" style={{ marginTop: 14 }}>
          <li>
            <Link
              to="/docs/guides/pricing-models"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Choose a pricing model ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/guides/launch-bundle"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Launch a new Bundle ›
            </Link>
          </li>
        </ul>
      </div>

      <div className="dp-section">
        <h2>
          <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>06</span>
          Numbers you can trust
        </h2>
        <p className="dp-plain">
          The Assistant never quotes figures from memory. Every number — MRR, ARR, churn, an invoice
          balance — comes from a live query against your data at the moment you ask, using the same
          analytics engine the dashboard does. Knowledge questions cite the documentation they draw
          from, so you can read further.
        </p>
      </div>

      <div className="dp-section">
        <h2>
          <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>07</span>
          What it remembers
        </h2>
        <p className="dp-plain">
          Tell the Assistant something that should hold beyond one conversation, such as how you
          like answers or a rule of the business (&ldquo;we never discount more than 20%&rdquo;),
          and it offers to remember it on a card. Nothing is saved until you approve. Every later
          conversation starts with what was saved and with your Business profile (Org Settings,
          Business), so its examples and suggestions fit your business.
        </p>
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          <div className="dp-field-row">
            <div className="dp-field-k">For you</div>
            <div className="dp-field-v">
              Shapes only your own conversations. Nobody else sees it, admins included.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">For everyone</div>
            <div className="dp-field-v">
              Shapes every member&rsquo;s conversations. Only workspace admins can save, change or
              remove one.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Limits</div>
            <div className="dp-field-v">
              One sentence each, up to 300 characters. Up to 50 for the workspace and 30 per person.
              Saving the same words twice keeps one.
            </div>
          </div>
        </div>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Ask &ldquo;what do you remember?&rdquo; to see it all, then ask the Assistant to reword or
          forget one; each change is a card you approve. The same list is under Org Settings,
          General, Agent memory, where you can also edit in place and choose{' '}
          <strong>Show what the agent sees</strong> to read exactly what the Assistant is given at
          the start of every turn. What is saved is treated as information from your team, not as
          instructions: it cannot change what the Assistant is allowed to do. When you ask for
          something a memory rules out, it does what you asked and says which memory it departs
          from. Claude and other MCP clients do not receive memories.
        </p>
      </div>

      <div className="dp-section">
        <h2>
          <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>08</span>
          Outside the dashboard
        </h2>
        <p className="dp-plain">
          The same tools work from Claude or any MCP client &mdash; clients, subscriptions,
          invoices, live MRR, catalog, OKR hypotheses, and writes such as entering a missing
          in-arrears usage quantity or proposing hypotheses for a Key Result. Mutating tools return
          a proposal; the credential owner must approve it while signed in to Revvly. The MCP client
          cannot approve its own request. The full list of Assistant tools is in{' '}
          <Link to="/docs/reference/assistant-tools">what the Assistant can do</Link>. See{' '}
          <Link to="/docs/api/mcp">Connect Claude with MCP</Link>.
        </p>
      </div>
    </div>
  );
}
